import Styles from './applicationRounds.module.css';
import React, { useEffect, useState } from 'react';
import { collection, getFirestore, onSnapshot } from 'firebase/firestore';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { examinationLabel, overallLabel } from '../types';

type RoundEvaluation = {
  id: string;
  stage: number | null;
  judgeName: string;
  draft: boolean;
  overallScore: number | null;
  overallComment: string;
};

type Round = {
  id: string;
  round: number;
  periodName: string;
  resultLabel: string;
  result: number | null;
  reachedStage: number | null;
  appliedAt: Date | null;
  concludedAt: Date | null;
  closed: boolean;
  salonMemo: string;
  evaluations: RoundEvaluation[];
};

const toDate = (v: any): Date | null => (v?.toDate ? v.toDate() : null);
const fmt = (d: Date | null) => (d ? `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}` : '-');

// 募集回ごとの応募の記録(applicants/{id}/rounds、Cloud Functions の applicationArchive.js が作る)。
// 結論(採用・不採用・辞退)が出た時点・募集回を締めた時点の、結果と審査員の評価を残している。
// サロンのownerだけが見られる(審査員には過去の履歴を見せない。呼び出し側とルールの両方で制限)。
const ApplicationRounds: React.FC<{ applicantId: string }> = ({ applicantId }) => {
  const [ rounds, setRounds ] = useState<Round[]>([]);
  const [ openId, setOpenId ] = useState<string | null>(null);

  useEffect(() => {
    setRounds([]);
    return onSnapshot(
      collection(getFirestore(), 'applicants', applicantId, 'rounds'),
      (snap) => setRounds(snap.docs.map((d) => {
        const r = d.data();
        return {
          id: d.id,
          round: r.round ?? Number(d.id),
          periodName: r.periodName ?? `第${d.id}回募集`,
          resultLabel: r.resultLabel ?? '結論なし',
          result: typeof r.result === 'number' ? r.result : null,
          reachedStage: typeof r.reachedStage === 'number' ? r.reachedStage : null,
          appliedAt: toDate(r.appliedAt),
          concludedAt: toDate(r.concludedAt),
          closed: !!r.closed,
          salonMemo: r.salonMemo ?? '',
          evaluations: (Array.isArray(r.evaluations) ? r.evaluations : []).map((e: any) => ({
            id: e.id ?? '',
            stage: typeof e.stage === 'number' ? e.stage : null,
            judgeName: e.judgeName ?? '',
            draft: !!e.draft,
            overallScore: typeof e.overallScore === 'number' ? e.overallScore : null,
            overallComment: e.overallComment ?? '',
          })),
        };
      }).sort((a, b) => b.round - a.round)),
      (error) => console.error('応募の記録を読み込めませんでした', error),
    );
  }, [applicantId]);

  if (rounds.length === 0) return null;

  return (
    <div className={Styles.section}>
      <h5 className={Styles.title}>募集回ごとの記録</h5>
      <p className={Styles.lead}>結論が出た時点・募集回を締めた時点の記録です。審査員には表示されません。</p>
      {rounds.map((r) => {
        const open = openId === r.id;
        const evaluations = r.evaluations.filter((e) => !e.draft).sort((a, b) => (a.stage ?? 0) - (b.stage ?? 0));
        return (
          <div key={r.id} className={Styles.card}>
            <button type="button" className={Styles.head} onClick={() => setOpenId(open ? null : r.id)}>
              <span className={Styles.period}>{r.periodName}</span>
              <span className={r.result === 6 ? Styles.resultDone : r.result === null ? Styles.resultNone : Styles.resultClosed}>
                {r.resultLabel}
              </span>
              {r.reachedStage && r.result !== 6 && <span className={Styles.reached}>{examinationLabel(r.reachedStage)}まで</span>}
              {r.closed && <span className={Styles.closed}>締め済み</span>}
              {open ? <IoChevronUp className={Styles.chevron} /> : <IoChevronDown className={Styles.chevron} />}
            </button>
            {open && (
              <div className={Styles.body}>
                <p className={Styles.row}><span>応募日</span>{fmt(r.appliedAt)}</p>
                <p className={Styles.row}><span>結論が出た日</span>{fmt(r.concludedAt)}</p>
                {r.salonMemo && <p className={Styles.row}><span>メモ</span>{r.salonMemo}</p>}
                <p className={Styles.subTitle}>審査員の評価（{evaluations.length}件）</p>
                {evaluations.length === 0 ? (
                  <p className={Styles.empty}>評価はありません。</p>
                ) : evaluations.map((e) => (
                  <div key={e.id} className={Styles.evaluation}>
                    <p className={Styles.evaluationHead}>
                      {e.stage ? examinationLabel(e.stage) : ''}・{e.judgeName || '審査員'}
                      <strong>{e.overallScore !== null ? overallLabel(e.overallScore) : '未入力'}</strong>
                    </p>
                    {e.overallComment && <p className={Styles.evaluationComment}>{e.overallComment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ApplicationRounds;
